//公式の playground を基に作成。差分が大きいため、追従は困難ないし不要と思われる
//https://github.com/facebook/lexical/tree/main/packages/lexical-playground/src/plugins/EmojisPlugin
/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * Modified by TMC. 2023/08
 */
import {
  $createTextNode,
  $insertNodes,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalCommand,
  LexicalEditor,
} from 'lexical'

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { TextNode } from 'lexical'
import { useEffect } from 'react'
import { $createEmojiNode, EmojiNode } from '../../nodes/emojiNode'
import { getEmojiByName } from '@crew/emoji-data'
import { SPACE_CHARACTER } from 'configs/constants'

export const INSERT_EMOJI_COMMAND: LexicalCommand<string> = createCommand()

// 絵文字文字列（short name）検出用正規表現
// 例）:strawberry:
const emojiRegExp = new RegExp(':([a-zA-Z0-9_\\-]+?):')

function findAndTransformEmoji(node: TextNode): null | TextNode {
  const text = node.getTextContent()

  const match = emojiRegExp.exec(text)
  if (!match) {
    return null
  }

  // 入力 ⇒ XXXXXX:strawberry:XXXXXX
  // match[0] ⇒ :strawberry:
  const matchWord = match[0]
  // match[1] ⇒ strawberry
  const emojiShortName = match[1]

  // short nameから絵文字情報を検索
  const emoji = getEmojiByName(emojiShortName)
  if (emoji != null) {
    let targetNode
    // TextNodeを分割する、置き換え対象の部分を抜き出す
    if (match.index === 0) {
      // 該当箇所が先頭だった場合は、2分割で先頭のNodeが置き換え対象
      ;[targetNode] = node.splitText(match.index + matchWord.length)
    } else {
      // それ以外は、前・該当箇所・後 の3分割で2番目のNodeが置き換え対象
      ;[, targetNode] = node.splitText(
        match.index,
        match.index + matchWord.length
      )
    }

    // EmojiNodeを生成
    const emojiNode = $createEmojiNode(emoji.short_name)
    // 置き換え対象のTextNodeを生成したEmojiNodeで置き換える
    targetNode.replace(emojiNode)
  }
  return null
}

function textNodeTransform(node: TextNode): void {
  let targetNode: TextNode | null = node

  // findAndTransformEmoji でNode分割を行う可能性があり
  // 分割した後のNode達もチェック対象とするためwhileでまわす
  while (targetNode !== null) {
    if (!targetNode.isSimpleText()) {
      return
    }

    targetNode = findAndTransformEmoji(targetNode)
  }
}

// 入力用
function useEmojisNodeTransform(editor: LexicalEditor): void {
  useEffect(() => {
    if (!editor.hasNodes([EmojiNode])) {
      throw new Error('EmojisPlugin: EmojiNode not registered on editor')
    }

    return editor.registerNodeTransform(TextNode, textNodeTransform)
  }, [editor])
}

// ダイアログから挿入用
function useEmojisCommand(editor: LexicalEditor): void {
  useEffect(() => {
    if (!editor.hasNodes([EmojiNode])) {
      throw new Error('EmojisPlugin: EmojiNode not registered on editor')
    }

    // 絵文字を挿入するコマンドを定義する
    return editor.registerCommand(
      INSERT_EMOJI_COMMAND,
      (payload: string) => {
        const emojiNode = $createEmojiNode(payload)
        $insertNodes([
          emojiNode,
          $createTextNode(SPACE_CHARACTER), // 絵文字の後ろに半角スペースを挿入
        ])
        return true
      },
      COMMAND_PRIORITY_EDITOR
    )
  }, [editor])
}

/**
 * 絵文字プラグイン
 * @returns
 */
export function EmojiPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  // 入力用
  useEmojisNodeTransform(editor)
  // ダイアログから挿入用
  useEmojisCommand(editor)
  return null
}
